import type { DateTime } from "luxon"

import type { DateTimeFactory } from "../../../localization/useDateTime"
import type { WeekDays } from "../productGroup/productGroupTypes"

import { isWeekDay } from "./helpers"
import type { AvailabilityDetails, ProductGroupAvailability } from "./productsTypes"

export type TimeOfDayInterval = Readonly<{ start: DateTime; end: DateTime }>

/** Empty week days fall back to the group's days, a missing time interval to the group's hours. */
export type NarrowedAvailability = Readonly<{
    weekDays: WeekDays[]
    timeInterval: TimeOfDayInterval | undefined
    /** the product shares no days or no hours with the group, so it falls back to the group's */
    hasNoOverlap: boolean
}>

const toMinuteOfDay = (time: DateTime): number => time.hour * 60 + time.minute

export const toTimeOfDayInterval = (
    interval: Readonly<{ start: string; end: string }> | undefined,
    dateTimeFactory: DateTimeFactory,
): TimeOfDayInterval | undefined => {
    if (!interval) return undefined

    const start = dateTimeFactory.fromISO(interval.start)
    const end = dateTimeFactory.fromISO(interval.end)

    return start.isValid && end.isValid ? { start, end } : undefined
}

/** Empty when the product group does not limit the days. */
export const getProductGroupWeekDays = (groupAvailability: ProductGroupAvailability | undefined): WeekDays[] =>
    (groupAvailability?.weekDays ?? []).filter(isWeekDay)

export const getProductGroupTimeInterval = (
    groupAvailability: ProductGroupAvailability | undefined,
    dateTimeFactory: DateTimeFactory,
): TimeOfDayInterval | undefined => toTimeOfDayInterval(groupAvailability?.timeInterval, dateTimeFactory)

export const isWithinGroupWeekDays = (
    weekDays: ReadonlyArray<WeekDays>,
    groupWeekDays: ReadonlyArray<WeekDays>,
): boolean => !groupWeekDays.length || weekDays.every((day) => groupWeekDays.includes(day))

export const isWithinGroupTimeInterval = (
    timeInterval: TimeOfDayInterval | undefined,
    groupTimeInterval: TimeOfDayInterval | undefined,
): boolean =>
    !timeInterval ||
    !groupTimeInterval ||
    (toMinuteOfDay(timeInterval.start) >= toMinuteOfDay(groupTimeInterval.start) &&
        toMinuteOfDay(timeInterval.end) <= toMinuteOfDay(groupTimeInterval.end))

function narrowTimeInterval(
    timeInterval: TimeOfDayInterval | undefined,
    groupTimeInterval: TimeOfDayInterval | undefined,
): TimeOfDayInterval | undefined {
    if (!timeInterval || !groupTimeInterval) return timeInterval

    const start =
        toMinuteOfDay(timeInterval.start) < toMinuteOfDay(groupTimeInterval.start)
            ? groupTimeInterval.start
            : timeInterval.start
    const end =
        toMinuteOfDay(timeInterval.end) > toMinuteOfDay(groupTimeInterval.end)
            ? groupTimeInterval.end
            : timeInterval.end

    return toMinuteOfDay(start) < toMinuteOfDay(end) ? { start, end } : undefined
}

/** Undefined when the product's own days and hours already fit the group. */
export function narrowToProductGroup(
    productAvailability: AvailabilityDetails | undefined,
    groupAvailability: ProductGroupAvailability | undefined,
    dateTimeFactory: DateTimeFactory,
): NarrowedAvailability | undefined {
    const weekDays = (productAvailability?.weekDays ?? []).filter(isWeekDay)
    const timeInterval = toTimeOfDayInterval(productAvailability?.timeInterval, dateTimeFactory)
    const groupWeekDays = getProductGroupWeekDays(groupAvailability)
    const groupTimeInterval = getProductGroupTimeInterval(groupAvailability, dateTimeFactory)

    if (isWithinGroupWeekDays(weekDays, groupWeekDays) && isWithinGroupTimeInterval(timeInterval, groupTimeInterval)) {
        return undefined
    }

    const narrowedWeekDays = groupWeekDays.length ? weekDays.filter((day) => groupWeekDays.includes(day)) : weekDays
    const narrowedTimeInterval = narrowTimeInterval(timeInterval, groupTimeInterval)

    return {
        weekDays: narrowedWeekDays,
        timeInterval: narrowedTimeInterval,
        hasNoOverlap:
            (weekDays.length > 0 && !narrowedWeekDays.length) ||
            (timeInterval !== undefined && narrowedTimeInterval === undefined),
    }
}
